import { useMemo } from 'react';
import type { Specialty } from '@/lib/api/specialties';

/** Most suggestions shown at once — the catalogue can be large. */
const MAX_SUGGESTIONS = 50;

/**
 * Catalogue entries matching `query`, those starting with it first, capped at
 * {@link MAX_SUGGESTIONS}. The lower-cased labels are built once per catalogue,
 * so each keystroke is a single pass over plain strings.
 */
export function useSpecialtyMatches(specialties: Specialty[], query: string): Specialty[] {
  const index = useMemo(
    () => specialties.map((specialty) => ({ specialty, label: specialty.display.toLowerCase() })),
    [specialties]
  );

  return useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return index.slice(0, MAX_SUGGESTIONS).map((entry) => entry.specialty);

    const starts: Specialty[] = [];
    const contains: Specialty[] = [];
    for (const entry of index) {
      if (entry.label.startsWith(needle)) starts.push(entry.specialty);
      else if (entry.label.includes(needle)) contains.push(entry.specialty);
      if (starts.length >= MAX_SUGGESTIONS) break;
    }
    return [...starts, ...contains].slice(0, MAX_SUGGESTIONS);
  }, [index, query]);
}
